/* =========================================================
   "Verificar" — the floating document viewer with its reading
   tracker.
     PDF  → a page counts once it dwells visibly in the viewer
     DOCX → progress follows scroll depth
   Reaching 100% stamps the verification date and unlocks the
   download button.
   ========================================================= */
import React from 'react';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import type { ComplianceDoc } from '@/types';
import { ic, sized } from '@/components/Icons';
import { Modal } from '@/components/ui';
import { ds } from '@/data';
import { useI18n } from '@/i18n';
import { docxToHtml, openPdf } from '@/lib/docViewers';
import { useAppStore } from '@/store/useAppStore';
import { pctColor, triggerDownload } from './helpers';

/* ---------- one rendered PDF page ---------- */

interface PdfPageProps {
  pdf: PDFDocumentProxy;
  num: number;
  width: number;
  scrollRootRef: React.RefObject<HTMLDivElement | null>;
  read: boolean;
  onRead: (n: number) => void;
  registerEl: (n: number, el: HTMLDivElement | null) => void;
}

function PdfPage({ pdf, num, width, scrollRootRef, read, onRead, registerEl }: PdfPageProps) {
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [shouldRender, setShouldRender] = React.useState(false);
  const [dims, setDims] = React.useState<{ w: number; h: number } | null>(null);
  const renderTask = React.useRef<RenderTask | null>(null);

  React.useEffect(() => {
    registerEl(num, wrapRef.current);
    return () => registerEl(num, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // lazy render: only rasterize pages near the viewport
  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShouldRender(true);
            io.disconnect();
          }
        });
      },
      { root: scrollRootRef.current || null, rootMargin: '700px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // read tracking: a page counts once it dwells visibly in the viewer
  React.useEffect(() => {
    if (read) return;
    const el = wrapRef.current;
    if (!el) return;
    const root = scrollRootRef.current || null;
    let timer: number | null = null;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const rootH = root ? root.clientHeight : window.innerHeight;
          const enough = e.intersectionRatio >= 0.45 || e.intersectionRect.height >= rootH * 0.65;
          if (e.isIntersecting && enough) {
            if (!timer) {
              timer = window.setTimeout(() => {
                timer = null;
                onRead(num);
              }, 700);
            }
          } else if (timer) {
            window.clearTimeout(timer);
            timer = null;
          }
        });
      },
      { root, threshold: [0, 0.25, 0.45, 0.7] },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      if (timer) window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [read]);

  // rasterize (and re-rasterize on zoom changes)
  React.useEffect(() => {
    if (!shouldRender || !pdf) return;
    let cancelled = false;
    void (async () => {
      try {
        const page = await pdf.getPage(num);
        if (cancelled) return;
        const vp1 = page.getViewport({ scale: 1 });
        const scale = width / vp1.width;
        const vp = page.getViewport({ scale });
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = Math.floor(vp.width * dpr);
        canvas.height = Math.floor(vp.height * dpr);
        canvas.style.width = Math.floor(vp.width) + 'px';
        canvas.style.height = Math.floor(vp.height) + 'px';
        setDims({ w: Math.floor(vp.width), h: Math.floor(vp.height) });
        if (renderTask.current) {
          try { renderTask.current.cancel(); } catch { /* already done */ }
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        renderTask.current = page.render({
          canvasContext: ctx,
          viewport: vp,
          transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined,
        });
        await renderTask.current.promise;
      } catch {
        /* cancelled mid-render — fine */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shouldRender, pdf, width, num]);

  return (
    <div
      ref={wrapRef}
      data-page={num}
      style={{
        position: 'relative', background: '#fff', borderRadius: 3, flexShrink: 0,
        boxShadow: '0 2px 12px rgba(0,0,0,.30)',
        width: dims ? dims.w : width,
        minHeight: dims ? undefined : Math.round(width * 1.294),
      }}
    >
      {!dims ? <div className="skel" style={{ position: 'absolute', inset: 10, borderRadius: 4 }} /> : null}
      <canvas ref={canvasRef} style={{ display: 'block', borderRadius: 3 }} />
      <div
        style={{
          position: 'absolute', top: 8, right: 8, display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '3px 8px', borderRadius: 999, fontSize: 10.5, fontWeight: 700,
          background: read ? 'var(--ok)' : 'rgba(27,35,48,.72)', color: '#fff',
          boxShadow: '0 1px 4px rgba(0,0,0,.25)', transition: 'background .25s ease',
        }}
      >
        {read ? sized(ic.check, 11) : null}
        <span className="mono">{num}</span>
      </div>
    </div>
  );
}

/* ---------- per-page read rail (jump + status) ---------- */

function PageRail({
  numPages, readPages, current, onJump,
}: {
  numPages: number;
  readPages: Set<number>;
  current: number;
  onJump: (n: number) => void;
}) {
  const { lang } = useI18n();
  return (
    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
      {Array.from({ length: numPages }, (_, i) => i + 1).map((n) => {
        const read = readPages.has(n);
        const cur = n === current;
        return (
          <button
            key={n}
            onClick={() => onJump(n)}
            data-tip={(lang === 'es' ? 'Página ' : 'Page ') + n + (read ? ' ✓' : '')}
            aria-label={(lang === 'es' ? 'Ir a página ' : 'Go to page ') + n}
            style={{
              width: 14, height: 14, borderRadius: 3, padding: 0, cursor: 'pointer',
              background: read ? 'var(--ok)' : 'var(--bg-2)',
              border: cur ? '2px solid var(--brand-primary)' : '1px solid ' + (read ? 'var(--ok-d)' : 'var(--line-2)'),
              transition: 'background .2s ease',
            }}
          />
        );
      })}
    </div>
  );
}

/* ---------- DOCX viewer with scroll-depth tracking ---------- */

function DocxViewer({
  html, zoom, savedPct, onProgress,
}: {
  html: string;
  zoom: number;
  savedPct: number;
  onProgress: (pct: number) => void;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const maxRef = React.useRef((savedPct || 0) / 100);

  const measure = React.useCallback(() => {
    const c = ref.current;
    if (!c) return;
    const frac = c.scrollHeight <= c.clientHeight + 4 ? 1 : Math.min(1, (c.scrollTop + c.clientHeight) / c.scrollHeight);
    if (frac > maxRef.current + 0.001) {
      maxRef.current = frac;
      onProgress(Math.min(100, Math.round(frac * 100)));
    }
  }, [onProgress]);

  React.useEffect(() => {
    const c = ref.current;
    if (!c) return;
    // restore an approximate resume point, then credit what is visible
    if (savedPct > 0 && savedPct < 100) {
      c.scrollTop = Math.max(0, (c.scrollHeight - c.clientHeight) * (savedPct / 100) - c.clientHeight * 0.5);
    }
    const t = window.setTimeout(() => measure(), 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={ref} onScroll={measure} style={{ maxHeight: '52vh', overflow: 'auto', background: '#414B5E', padding: 18, borderRadius: 8 }}>
      <div
        className="etc-docx"
        style={{
          maxWidth: 820, margin: '0 auto', background: '#fff', borderRadius: 3,
          boxShadow: '0 2px 12px rgba(0,0,0,.30)', padding: '46px 54px',
          fontSize: Math.round(14.5 * zoom * 10) / 10, lineHeight: 1.65, color: '#1B2330',
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

/* ---------- viewer modal ---------- */

export interface ViewerModalProps {
  orgId: string;
  doc: ComplianceDoc;
  onClose: () => void;
  onChanged: () => void;
}

export function ViewerModal({ orgId, doc, onClose, onChanged }: ViewerModalProps) {
  const { lang } = useI18n();
  const addToast = useAppStore((s) => s.addToast);
  const kind = doc.kind || (/\.docx$/i.test(doc.name) ? 'docx' : 'pdf');

  const [pdf, setPdf] = React.useState<PDFDocumentProxy | null>(null);
  const [numPages, setNumPages] = React.useState(0);
  const [readPages, setReadPages] = React.useState<Set<number>>(() => new Set(doc.pages || []));
  const [docxHtml, setDocxHtml] = React.useState<string | null>(null);
  const [docxPct, setDocxPct] = React.useState(doc.pct || 0);
  const [docxEpoch, setDocxEpoch] = React.useState(0); // bump to remount viewer on reset
  const [zoom, setZoom] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [curPage, setCurPage] = React.useState(1);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const pageEls = React.useRef<Record<number, HTMLDivElement>>({});
  const celebrated = React.useRef((doc.pct || 0) >= 100);

  // open the document on mount
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    // El documento puede no tener los bytes todavía: en modo API se
    // traen del servidor la primera vez que alguien lo abre.
    const bytes = ds.loadComplianceFile(orgId, doc.id);

    if (kind === 'docx') {
      bytes.then(docxToHtml)
        .then((html) => {
          if (cancelled) return;
          setDocxHtml(html);
          setDocxEpoch((e) => e + 1);
          setLoading(false);
        })
        .catch(() => {
          if (cancelled) return;
          setError(lang === 'es'
            ? 'No se pudo abrir el documento DOCX. Si acaba de publicarse, vuelve a intentarlo.'
            : 'Could not open the DOCX document. If it was just published, try again.');
          setLoading(false);
        });
    } else {
      bytes.then(openPdf)
        .then((d) => {
          if (cancelled) return;
          setPdf(d);
          setNumPages(d.numPages);
          setReadPages(new Set((doc.pages || []).filter((n) => n >= 1 && n <= d.numPages)));
          setLoading(false);
        })
        .catch(() => {
          if (cancelled) return;
          setError(lang === 'es' ? 'No se pudo abrir el documento PDF.' : 'Could not open the PDF document.');
          setLoading(false);
        });
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pct = kind === 'docx' ? docxPct : numPages > 0 ? Math.round((readPages.size / numPages) * 100) : doc.pct || 0;
  const docReady = kind === 'docx' ? !!docxHtml : numPages > 0;

  function celebrate() {
    if (celebrated.current) return;
    celebrated.current = true;
    addToast({
      kind: 'ok',
      celebrate: true,
      title: lang === 'es' ? 'Verificación completa — 100%' : 'Verification complete — 100%',
      body: (doc.title || doc.name) + (lang === 'es' ? ' · la descarga ya está habilitada.' : ' · download is now enabled.'),
    });
  }

  function markRead(num: number) {
    setReadPages((prev) => {
      if (prev.has(num)) return prev;
      const next = new Set(prev);
      next.add(num);
      ds.setComplianceProgress(orgId, doc.id, { pages: Array.from(next), total: numPages });
      onChanged();
      if (numPages > 0 && next.size === numPages) celebrate();
      return next;
    });
  }

  function onDocxProgress(p: number) {
    setDocxPct((prev) => {
      const next = Math.max(prev, p);
      ds.setComplianceProgress(orgId, doc.id, { pct: next });
      onChanged();
      if (next >= 100) celebrate();
      return next;
    });
  }

  function resetProgress() {
    ds.setComplianceProgress(orgId, doc.id, { pct: 0, pages: [], total: numPages || 0 });
    celebrated.current = false;
    setReadPages(new Set());
    setDocxPct(0);
    setDocxEpoch((e) => e + 1);
    onChanged();
    addToast({ kind: 'ok', title: lang === 'es' ? 'Verificación reiniciada' : 'Verification reset' });
  }

  function registerEl(num: number, el: HTMLDivElement | null) {
    if (el) pageEls.current[num] = el;
    else delete pageEls.current[num];
  }

  function jumpTo(num: number) {
    const c = scrollRef.current;
    const el = pageEls.current[num];
    if (c && el) c.scrollTo({ top: Math.max(0, el.offsetTop - 14), behavior: 'smooth' });
  }

  function onScroll() {
    const c = scrollRef.current;
    if (!c) return;
    const mid = c.scrollTop + c.clientHeight / 2;
    let cur = 1;
    Object.keys(pageEls.current).forEach((k) => {
      const el = pageEls.current[Number(k)];
      if (el && el.offsetTop <= mid) cur = Math.max(cur, Number(k));
    });
    setCurPage(cur);
  }

  const pageWidth = Math.round(700 * zoom);

  return (
    <Modal
      open
      onClose={onClose}
      width={880}
      headerStyle={{ background: 'var(--modal-head)', borderBottom: '1px solid var(--act-download-d)', color: '#fff' }}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {doc.title || doc.name}
          <span className="chip lock mono" style={{ fontSize: 9, background: 'rgba(255,255,255,.18)', borderColor: 'rgba(255,255,255,.35)', color: '#fff' }}>
            {kind.toUpperCase()}
          </span>
        </span>
      }
      footer={
        <>
          {pct > 0 && pct < 100 ? (
            <button className="btn ghost" onClick={resetProgress} data-tip={lang === 'es' ? 'Reiniciar seguimiento de verificación' : 'Reset verification tracker'}>
              {sized(ic.refresh, 13)} {lang === 'es' ? 'Reiniciar' : 'Reset'}
            </button>
          ) : null}
          <span
            data-tip={
              pct < 100
                ? lang === 'es' ? 'Se habilita al completar la verificación (100%)' : 'Enabled once verification reaches 100%'
                : lang === 'es' ? 'Descargar a tu computadora' : 'Download to your computer'
            }
          >
            <button
              className="btn"
              disabled={pct < 100}
              onClick={() => {
                void ds.loadComplianceFile(orgId, doc.id)
                  .then((url) => triggerDownload(doc, url))
                  .catch(() => setError(lang === 'es'
                    ? 'No se pudo descargar el archivo.'
                    : 'Could not download the file.'));
                addToast({ kind: 'template', title: lang === 'es' ? 'Descarga iniciada' : 'Download started', body: doc.name });
              }}
            >
              {sized(ic.download, 13)} {lang === 'es' ? 'Descarga' : 'Download'}
            </button>
          </span>
          <button className="btn primary" onClick={onClose}>{lang === 'es' ? 'Cerrar' : 'Close'}</button>
        </>
      }
    >
      {/* verification tracker strip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ flex: '1 1 220px', minWidth: 200 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
              {lang === 'es' ? 'Verificación de lectura' : 'Reading verification'}
            </span>
            <span className="mono" style={{ fontSize: 13, fontWeight: 800, color: pctColor(pct) }}>{pct}%</span>
          </div>
          <div style={{ background: 'var(--bg-2)', borderRadius: 99, height: 6, overflow: 'hidden' }}>
            <div style={{ width: pct + '%', background: pctColor(pct), height: '100%', borderRadius: 99, transition: 'width .3s ease' }} />
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 5 }}>
            {kind === 'docx'
              ? lang === 'es' ? 'Avance por desplazamiento — llega al final para completar' : 'Scroll-depth progress — reach the end to complete'
              : lang === 'es' ? `${readPages.size} de ${numPages || '—'} páginas verificadas` : `${readPages.size} of ${numPages || '—'} pages verified`}
          </div>
        </div>

        {kind === 'pdf' && numPages > 0 ? (
          <div style={{ flex: '2 1 220px' }}>
            <PageRail numPages={numPages} readPages={readPages} current={curPage} onJump={jumpTo} />
          </div>
        ) : null}

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <button className="btn sm icon" onClick={() => setZoom((z) => Math.max(0.6, Math.round((z - 0.2) * 10) / 10))} disabled={zoom <= 0.6} aria-label="Zoom out">−</button>
          <span className="mono" style={{ fontSize: 11.5, minWidth: 38, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
          <button className="btn sm icon" onClick={() => setZoom((z) => Math.min(2, Math.round((z + 0.2) * 10) / 10))} disabled={zoom >= 2} aria-label="Zoom in">+</button>
        </div>
      </div>

      {/* viewer */}
      {error ? (
        <div style={{ padding: '30px 16px', textAlign: 'center', color: 'var(--err)', fontSize: 13 }}>
          {ic.warn} {error}
        </div>
      ) : loading || !docReady ? (
        <div style={{ padding: '40px 16px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
          <div className="skel" style={{ width: 220, height: 14, margin: '0 auto 10px' }} />
          {lang === 'es' ? 'Abriendo documento…' : 'Opening document…'}
        </div>
      ) : kind === 'docx' ? (
        <DocxViewer key={'docx-' + docxEpoch} html={docxHtml!} zoom={zoom} savedPct={docxPct} onProgress={onDocxProgress} />
      ) : (
        <div
          ref={scrollRef}
          onScroll={onScroll}
          style={{ position: 'relative', maxHeight: '52vh', overflow: 'auto', background: '#414B5E', padding: 18, borderRadius: 8 }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center', minWidth: 'min-content' }}>
            {Array.from({ length: numPages }, (_, i) => i + 1).map((n) => (
              <PdfPage key={n} pdf={pdf!} num={n} width={pageWidth} scrollRootRef={scrollRef} read={readPages.has(n)} onRead={markRead} registerEl={registerEl} />
            ))}
          </div>
          <div
            style={{
              position: 'sticky', bottom: 8, left: '50%', display: 'table', margin: '12px auto 0',
              padding: '5px 12px', borderRadius: 999, background: 'rgba(27,35,48,.85)', color: '#fff',
              fontSize: 11.5, fontWeight: 700, pointerEvents: 'none',
            }}
          >
            <span className="mono">{curPage} / {numPages}</span>
          </div>
        </div>
      )}

      {docReady && !error ? (
        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ display: 'inline-flex' }}>{sized(ic.info, 13)}</span>
          {pct === 100
            ? lang === 'es' ? 'Documento verificado en su totalidad — la descarga está habilitada.' : 'Document fully verified — download is enabled.'
            : kind === 'docx'
              ? lang === 'es' ? 'Desplázate por el documento: el avance se registra según la profundidad de lectura alcanzada.' : 'Scroll through the document: progress is recorded by the reading depth reached.'
              : lang === 'es' ? 'Desplázate por el documento: cada página se marca como verificada al permanecer visible en el visor.' : 'Scroll through the document: each page is marked as verified once it stays visible in the viewer.'}
        </div>
      ) : null}
    </Modal>
  );
}
